import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, Search, Copy } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import processTemplateService from '../../services/processTemplateService';

const ProcessTemplateManager = ({ open, onClose }) => {
  const [, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    tool_category: '',
    diameter: '',
    total_length: '',
    edge_length: '',
    search: ''
  });
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tool_category: '',
    diameter: '',
    total_length: '',
    edge_length: '',
    bar_price: '',
    steps: []
  });

  // Use props if provided, otherwise fall back to internal state
  const isDialogMode = open !== undefined && onClose !== undefined;
  const dialogOpen = isDialogMode ? open : internalDialogOpen;
  const setDialogOpen = isDialogMode ? onClose : setInternalDialogOpen;

  useEffect(() => {
    loadTemplates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      // Build filter params for API
      const filterParams = {};
      if (filters.tool_category) filterParams.tool_category = filters.tool_category;
      if (filters.diameter) filterParams.diameter = parseFloat(filters.diameter);
      if (filters.total_length) filterParams.total_length = parseInt(filters.total_length);
      if (filters.edge_length) filterParams.edge_length = parseInt(filters.edge_length);
      if (filters.search) filterParams.search = filters.search;
      
      const data = await processTemplateService.getTemplates(filterParams);
      setTemplates(data);
      setFilteredTemplates(data);
    } catch (error) {
      console.error('Failed to load templates', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        description: template.description || '',
        tool_category: template.tool_category || '',
        diameter: template.diameter || '',
        total_length: template.total_length || '',
        edge_length: template.edge_length || '',
        bar_price: template.bar_price || '',
        steps: template.steps ? [...template.steps] : []
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        name: '',
        description: '',
        tool_category: '',
        diameter: '',
        total_length: '',
        edge_length: '',
        bar_price: '',
        steps: []
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTemplate(null);
    setFormData({ 
      name: '', 
      description: '', 
      tool_category: '',
      diameter: '',
      total_length: '',
      edge_length: '',
      bar_price: '',
      steps: [] 
    });
  };

  const handleSave = async () => {
    try {
      if (editingTemplate) {
        await processTemplateService.updateTemplate(editingTemplate._id, formData);
      } else {
        await processTemplateService.createTemplate(formData);
      }
      loadTemplates();
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save template', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await processTemplateService.deleteTemplate(id);
        loadTemplates();
      } catch (error) {
        console.error('Failed to delete template', error);
      }
    }
  };

  const handleClone = async (template) => {
    try {
      const cloned = await processTemplateService.cloneTemplate(template._id);
      if (cloned) {
        // Open edit dialog with cloned template
        handleOpenDialog(cloned);
        // Reload templates list
        loadTemplates();
      }
    } catch (error) {
      console.error('Failed to clone template', error);
      alert('克隆模板失败: ' + (error.message || '未知错误'));
    }
  };

  const handleAddStep = () => {
    setFormData({
      ...formData,
      steps: [
        ...formData.steps,
        {
          sequence: formData.steps.length + 1,
          name: '',
          standard_time: 0,
          actions: ''
        }
      ]
    });
  };

  const handleRemoveStep = (index) => {
    const newSteps = formData.steps.filter((_, i) => i !== index);
    const resequencedSteps = newSteps.map((step, i) => ({ ...step, sequence: i + 1 }));
    setFormData({ ...formData, steps: resequencedSteps });
  };

  const handleStepChange = (index, field, value) => {
    const newSteps = [...formData.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setFormData({ ...formData, steps: newSteps });
  };

  const handleMoveStep = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === formData.steps.length - 1)
    ) {
      return;
    }

    const newSteps = [...formData.steps];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
    
    const resequencedSteps = newSteps.map((step, i) => ({ ...step, sequence: i + 1 }));
    setFormData({ ...formData, steps: resequencedSteps });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">工序模板管理</h2>
          <p className="text-muted-foreground">管理生产工艺流程模板</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="mr-2 h-4 w-4" />
          新建模板
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center bg-muted/50 p-4 rounded-lg">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">筛选:</span>
        </div>
        
        {/* Tool Category Filter */}
        <div className="flex gap-2 items-center">
          <span className="text-sm text-muted-foreground">工具类别:</span>
          <select
            value={filters.tool_category}
            onChange={(e) => setFilters({...filters, tool_category: e.target.value})}
            className="px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">全部</option>
            <option value="钻头">钻头</option>
            <option value="扩铰刀">扩铰刀</option>
            <option value="铰刀">铰刀</option>
            <option value="锪窝钻">锪窝钻</option>
          </select>
        </div>

        {/* Diameter Filter */}
        <div className="flex gap-2 items-center">
          <span className="text-sm text-muted-foreground">直径:</span>
          <input
            type="number"
            step="0.1"
            placeholder="如: 3.5"
            value={filters.diameter}
            onChange={(e) => setFilters({...filters, diameter: e.target.value})}
            className="w-24 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <span className="text-sm text-muted-foreground">mm</span>
        </div>

        {/* Total Length Filter */}
        <div className="flex gap-2 items-center">
          <span className="text-sm text-muted-foreground">总长:</span>
          <select
            value={filters.total_length}
            onChange={(e) => setFilters({...filters, total_length: e.target.value})}
            className="px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">全部</option>
            <option value="95">95mm</option>
            <option value="150">150mm</option>
          </select>
        </div>

        {/* Edge Length Filter */}
        <div className="flex gap-2 items-center">
          <span className="text-sm text-muted-foreground">刃长:</span>
          <select
            value={filters.edge_length}
            onChange={(e) => setFilters({...filters, edge_length: e.target.value})}
            className="px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">全部</option>
            <option value="50">50mm</option>
            <option value="80">80mm</option>
          </select>
        </div>

        {/* Search */}
        <div className="flex gap-2 items-center flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="搜索模板名称..."
            value={filters.search}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
            className="flex-1 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Clear Filters */}
        {Object.values(filters).some(v => v !== '') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilters({
              tool_category: '',
              diameter: '',
              total_length: '',
              edge_length: '',
              search: ''
            })}
          >
            清除筛选
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          没有找到匹配的模板
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredTemplates.map((template) => (
            <Card key={template._id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {template.name}
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="编辑"
                    onClick={() => handleOpenDialog(template)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="克隆"
                    onClick={() => handleClone(template)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="删除"
                    className="text-destructive"
                    onClick={() => handleDelete(template._id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-2">
                  {template.description || '无描述'}
                </p>
                <div className="text-xs text-muted-foreground space-y-1">
                  {template.tool_category && <div>类别: {template.tool_category}</div>}
                  {template.diameter && <div>直径: {template.diameter}mm</div>}
                  {template.bar_price && <div>棒料价格: ¥{template.bar_price}</div>}
                </div>
                <div className="text-xs font-medium mt-2">
                  工序数量: {template.steps?.length || 0}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? '编辑工序模板' : '创建工序模板'}</DialogTitle>
            <DialogDescription>
              {editingTemplate ? '修改现有的工序模板信息和步骤' : '创建一个新的工序模板，定义生产步骤'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">模板名称</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="输入模板名称"
              />
            </div>
            
            {/* Tool specification fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="tool_category">工具类别</Label>
                <select
                  id="tool_category"
                  value={formData.tool_category}
                  onChange={(e) => setFormData({ ...formData, tool_category: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">选择类别</option>
                  <option value="钻头">钻头</option>
                  <option value="扩铰刀">扩铰刀</option>
                  <option value="铰刀">铰刀</option>
                  <option value="锪窝钻">锪窝钻</option>
                </select>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="diameter">直径 (mm)</Label>
                <Input
                  id="diameter"
                  type="number"
                  step="0.1"
                  value={formData.diameter}
                  onChange={(e) => setFormData({ ...formData, diameter: e.target.value })}
                  placeholder="如: 3.5"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="total_length">总长 (mm)</Label>
                <Input
                  id="total_length"
                  type="number"
                  value={formData.total_length}
                  onChange={(e) => setFormData({ ...formData, total_length: e.target.value })}
                  placeholder="如: 95"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="edge_length">刃长 (mm)</Label>
                <Input
                  id="edge_length"
                  type="number"
                  value={formData.edge_length}
                  onChange={(e) => setFormData({ ...formData, edge_length: e.target.value })}
                  placeholder="如: 50"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="bar_price">棒料价格 (¥)</Label>
                <Input
                  id="bar_price"
                  type="number"
                  step="0.01"
                  value={formData.bar_price}
                  onChange={(e) => setFormData({ ...formData, bar_price: e.target.value })}
                  placeholder="如: 153.05"
                />
              </div>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="description">描述</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="输入模板描述"
              />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>工序列表</Label>
                <Button variant="outline" size="sm" onClick={handleAddStep}>
                  <Plus className="mr-2 h-4 w-4" />
                  添加工序
                </Button>
              </div>
              
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[60px]">序号</TableHead>
                      <TableHead>工序名称</TableHead>
                      <TableHead className="w-[100px]">标准工时(分)</TableHead>
                      <TableHead>操作要求</TableHead>
                      <TableHead className="w-[120px]">操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.steps.map((step, index) => (
                      <TableRow key={index}>
                        <TableCell>{step.sequence}</TableCell>
                        <TableCell>
                          <Input
                            value={step.name}
                            onChange={(e) => handleStepChange(index, 'name', e.target.value)}
                            placeholder="工序名称"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={step.standard_time}
                            onChange={(e) => handleStepChange(index, 'standard_time', parseFloat(e.target.value) || 0)}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={step.actions}
                            onChange={(e) => handleStepChange(index, 'actions', e.target.value)}
                            placeholder="操作要求"
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleMoveStep(index, 'up')}
                              disabled={index === 0}
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleMoveStep(index, 'down')}
                              disabled={index === formData.steps.length - 1}
                            >
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive"
                              onClick={() => handleRemoveStep(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {formData.steps.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground">
                          暂无工序，请点击添加
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>取消</Button>
            <Button onClick={handleSave}>保存</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProcessTemplateManager;